/**
 * DELEGATE — the summary-suggestion read moved to `@heidi/evidence` at the
 * `evidence/source` carve. `GET /evidence/{sessionId}/summary-suggestion` still
 * returns the clinical signal score behind the Evidence banner (GRO-2767), over
 * the same `scribeHttpV2` instance.
 *
 * What stays here is the app-resolved gate: the pre-move `enabled` was
 * `!!token && !!sessionId && enabled`, and the token is the only app term. The
 * `!!sessionId` half deliberately did NOT move up here — it is about the
 * argument, not the app, so the package applies it and an empty id cannot fire a
 * GET at `/evidence//summary-suggestion`.
 *
 * The gate also carries the SURFACE term. `GET /v2/evidence/{sessionId}/summary-suggestion`
 * is part of ml-scribe's cloud-only evidence aggregate, so on an on-prem build
 * there is no such route — and the banner hook that calls this is mounted by
 * the in-session Ask Heidi bar on every session page, so without the term an
 * appliance with Evidence enabled 404s once per session.
 *
 * The signature is unchanged, so no call site moves.
 *
 * `EVIDENCE_SUMMARY_SUGGESTION_QUERY_KEY` is DECLARED in
 * `@heidi/evidence/contracts` and re-exported here name-intact.
 */
import { useIsAuthenticated } from '@/hooks/useIsAuthenticated';
import { isEvidenceApiAvailable } from '@/utils/backendSurfaces';
import { useQueryEvidenceSummarySuggestion as useQueryEvidenceSummarySuggestionCore } from '@heidi/evidence/data';

export { EVIDENCE_SUMMARY_SUGGESTION_QUERY_KEY } from '@heidi/evidence/contracts';
export type { EvidenceSummarySuggestionResponse } from '@heidi/evidence/contracts';

interface UseQueryEvidenceSummarySuggestionOptions {
  sessionId: string;
  enabled?: boolean;
}

export function useQueryEvidenceSummarySuggestion({
  sessionId,
  enabled = true,
}: UseQueryEvidenceSummarySuggestionOptions) {
  const { isAuthenticated } = useIsAuthenticated();

  return useQueryEvidenceSummarySuggestionCore({
    sessionId,
    enabled: isEvidenceApiAvailable && isAuthenticated && enabled,
  });
}
